<template>
  <div
    class="m-appointment-card flex flex-col"
    :class="{
      'with-hover cursor-pointer medicus-outline': onClick,
      grey: appointment?.displayStatus?.isEnded,
      'with-appointment-type-color': hasAppointmentTypeColor,
    }"
    :style="appointmentTypeColorStyle"
    role="button"
    tabindex="0"
    @click="onClick"
    @click.right.prevent="
      () => {
        allowRightClick ? handleAvailableRightClick() : undefined;
      }
    "
  >
    <slot>
      <template v-if="appointment">
        <m-layout-stack no-gap>
          <div class="card-title">
            <div class="appointment-info">
              <span v-if="appointment.start" class="time">{{ appointment.start }} – </span>
              <span>{{ appointment?.patient?.name }}</span>
            </div>
            <m-badge
              v-if="appointment.displayStatus && !appointment.displayStatus.isBooked"
              :text="appointment.displayStatus.label"
              :amber="displayStatusColour"
            />
          </div>
          <div v-if="appointment?.patient?.preferredName" class="card-description">
            <i
              >Preferred name <b>{{ appointment?.patient?.preferredName }}</b></i
            >
            <m-separator />
          </div>
        </m-layout-stack>

        <p v-if="!hideReasonForAppointment" class="card-description">
          <span>{{ appointment.compiledReasonForAppointment }}</span>
        </p>

        <p class="card-description">
          <span
            >{{ `${appointment.duration} min${appointment.duration === 1 ? "" : "s"}` }} •
            {{ appointment.deliveryMode.label }}</span
          >
        </p>

        <m-layout-stack class="card-footer" gap="1">
          <m-badge v-if="appointment.isHighPriority" text="High Priority" amber />

          <template v-for="(badge, i) in overlappingBadges" :key="i">
            <m-badge :text="badge" amber />
          </template>
        </m-layout-stack>
      </template>
    </slot>

    <MMenu v-model="isContextMenuShowing" no-auto-open>
      <slot name="context-menu-options"></slot>
    </MMenu>
  </div>
</template>

<script lang="ts">
import { PropType, defineComponent } from "vue";
import { randomID } from "../../../../../utils/helpers";
import MLayoutStack from "../../../MLayoutStack/MLayoutStack.vue";
import MBadge from "../../../MBadge";
import MSeparator from "../../../MSeparator/MSeparator.vue";
import { Appointment as AppointmentType } from "../types";
import MMenu from "../../../MMenu";

export default defineComponent({
  components: { MMenu, MLayoutStack, MSeparator, MBadge },

  props: {
    onClick: {
      type: Function as PropType<(e?: MouseEvent) => void>,
      required: false,
      default: () => () => {},
    },

    appointment: {
      required: false,
      type: Object as () => AppointmentType & {
        title?: string;
        description?: string;
        formattedDuration?: string;
      },
      default: null,
    },

    hideMoveAppointmentToSameDiaryOption: {
      required: false,
      type: Boolean,
      default: false,
    },

    hideReasonForAppointment: {
      required: false,
      type: Boolean,
      default: false,
    },

    allowRightClick: {
      required: false,
      type: Boolean,
      default: false,
    },
  },

  setup() {
    const controlId = randomID("m-appointment-card");

    return {
      controlId,
    };
  },

  data() {
    return {
      isContextMenuShowing: false,
    };
  },

  computed: {
    overlappingBadges() {
      if (!this.appointment.overlappingEntries) {
        return [];
      }
      let v = [];
      for (const a of this.appointment.overlappingEntries) {
        let b = "Overlapping Appointment";
        if ("rota" === a.type) {
          b = "Overlapping Activity";
        } else if ("break" === a.type) {
          b = "Overlapping Break";
        }
        if (-1 === v.indexOf(b)) {
          v.push(b);
        }
      }
      return v;
    },

    displayStatusColour(): boolean | undefined {
      return this.appointment?.displayStatus?.isDidNotAttend;
    },

    hasAppointmentTypeColor() {
      return (
        this.appointment?.diaryEntryType?.isAppointment !== false &&
        !!this.appointment?.appointmentType?.colour?.value
      );
    },

    appointmentTypeColorStyle() {
      return this.hasAppointmentTypeColor
        ? { "--appointment-type-color": this.appointment.appointmentType.colour.value }
        : {};
    },
  },

  methods: {
    handleAvailableRightClick() {
      this.isContextMenuShowing = !this.isContextMenuShowing;
    },
  },
});
</script>

<style lang="scss">
// BUTTON needs some special handling
button.m-appointment-card {
  // width: calc(100% - 30px);
  width: 100%;
  overflow: hidden;

  text-align: left;
}

.m-appointment-card {
  position: relative;
  border: none;
  padding: 2px 2px 2px var(--gap-2);
  background: #ffffff;
  border-radius: 4px;
  box-shadow: var(--box-shadow);
  flex-wrap: nowrap;
  user-select: none;
  border: 2px solid transparent;

  display: flex;
  flex: 0 0 auto;

  &.selected {
    background: var(--hover-background);
    border-radius: 8px;
  }
  &.ghost {
    opacity: 0.5;
    background: #c8ebfb;
  }
  &.grey {
    opacity: 0.4;
  }
  .card-title {
    display: flex;
    justify-content: space-between;
    align-items: flex-start;
  }

  .appointment-info {
    color: var(--theme-darkest-blue);
    transition: var(--transition);
    font-size: 14px;

    .time {
      font-weight: bold;
    }
  }

  .card-description {
    color: var(--theme-grey);
    word-break: break-word;
    hyphens: auto;
  }

  .card-footer {
    &:empty {
      display: none;
    }

    color: var(--theme-grey);
    margin-top: 0.2em;
    * {
      margin-right: 8px;
    }
  }

  &.with-appointment-type-color {
    &::before {
      content: "";
      position: absolute;
      left: -2px;
      top: -2px;
      bottom: -2px;
      width: 6px;
      background-color: var(--appointment-type-color);
      border-top-left-radius: 3px;
      border-bottom-left-radius: 3px;
    }

    .card-title,
    .card-description,
    .card-footer {
      margin-left: var(--gap-1);
    }
  }
}
.with-hover:hover {
  border-color: var(--theme-blue);

  .appointment-info {
    color: var(--theme-blue);
  }
}
</style>
